<template xmlns="http://www.w3.org/1999/html">
    <mini-calculator-page>
        <div slot="summary-panel">
            <div class="p-animated-calc__results a-form-inputs u-full-width u-margin-auto">
                <mini-calculator-header title="Period Between Two Dates" icon="period-dates"/>

                <control label="First Date" :no-flex-margin="true">
                    <div class="flex-grid flex-grid--reduced-bottom-margin-slight flex-grid--wrap">
                        <date-input v-model="firstDate"
                                    @input="calculate"
                                    ref="firstDate"
                                    v-field-control:first_date.required="{type:'date'}"></date-input>
                        <control-error :show="$error('first_date')">
                            The first date is required in the format DD/MM/YYYY
                        </control-error>
                        <div class="a-checkbox u-margin-tb-10-30">
                            <input autocomplete="off" type="checkbox" v-model="options.includeFirstDay"
                                   @change="updateOptions">
                            <label>Include first day</label>
                        </div>
                    </div>
                </control>

                <control label="Second Date" :no-flex-margin="true">
                    <div class="flex-grid flex-grid--reduced-bottom-margin-slight flex-grid--wrap">
                        <date-input v-model="secondDate"
                                    @input="calculate"
                                    ref="secondDate"
                                    v-field-control:second_date.required="{type:'date'}"></date-input>
                        <control-error :show="$error('second_date')">
                            The second date is required in the format DD/MM/YYYY
                        </control-error>
                        <div class="a-checkbox u-margin-tb-10-30">
                            <input autocomplete="off" type="checkbox" v-model="options.includeLastDay"
                                   @change="updateOptions">
                            <label>Include last day</label>
                        </div>
                    </div>
                </control>

                <control for="precision" label="Precision">
                    <select name="precision"
                            id="precision"
                            v-model="options.dp"
                            @change="calculate">
                        <option value="0">0</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                    </select>
                </control>

                <control label="Bank Holidays:" :no-flex-margin="true">
                    <div class="flex-grid flex-grid--reduced-margin-bottom flex-grid--wrap">
                        <div class="a-checkbox">
                            <input autocomplete="off" type="checkbox" v-model="options.includeBankHolidays" @change="updateOptions">
                            <label>Include bank holidays</label>
                        </div>
                    </div>
                </control>

                <control label="Weekends:" :no-flex-margin="true">
                    <div class="flex-grid flex-grid--reduced-margin-bottom flex-grid--wrap">
                        <div class="a-checkbox">
                            <input autocomplete="off" type="checkbox" v-model="options.includeWeekends" @change="updateOptions">
                            <label>Include Weekends</label>
                        </div>
                    </div>
                </control>

                <br/>

                <control label="Display Options:" :no-flex-margin="true">
                    <div class="flex-grid flex-grid--reduced-margin-bottom flex-grid--wrap flex-grid--row">
                        <div class="p-animated-calc--half p-animated-calc--half-non-block">
                            <div class="a-checkbox">
                                <input autocomplete="off" type="checkbox" v-model="options.show.years" @change="updateOptions">
                                <label>Years</label>
                            </div>
                        </div>
                        <div class="p-animated-calc--half p-animated-calc--half-non-block">
                            <div class="a-checkbox">
                                <input autocomplete="off" type="checkbox" v-model="options.show.months" @change="updateOptions">
                                <label>Months</label>
                            </div>
                        </div>
                        <div class="p-animated-calc--half p-animated-calc--half-non-block">
                            <div class="a-checkbox">
                                <input autocomplete="off" type="checkbox" v-model="options.show.weeks" @change="updateOptions">
                                <label>Weeks</label>
                            </div>
                        </div>
                        <div class="p-animated-calc--half p-animated-calc--half-non-block">
                            <div class="a-checkbox">
                                <input autocomplete="off" type="checkbox" v-model="options.show.days" @change="updateOptions">
                                <label>Days</label>
                            </div>
                        </div>
                    </div>
                </control>

                <h2 v-if="diffBetween">
                    Result
                </h2>

                <div class="flex-grid p-animated-calc--full-width flex-grid--row">

                    <div class="p-animated-calc--full-width">
                        <input autocomplete="off" type="text " disabled :value="diffBetween">
                    </div>

                </div>

                <div v-if="optionsEmpty()">
                    Please make sure you have selected display options...
                </div>
            </div>
        </div>

        <div slot="results-panel" class="flex-grid--vertical" v-show="showResults">
            <div class="p-animated-calc__results-panel u-full-width u-margin-auto">
                <h2>
                    Explanation
                </h2>

                <p>
                    The duration between <strong>{{ firstDate }}</strong> and <strong>{{ secondDate }}</strong>
                    has been calculated.
                </p>

                <p>
                    During the period <strong>{{ options.numberOfBankHolidays }}</strong> number of bank holidays would have
                    occurred. These have been <span v-if="options.includeBankHolidays"> included</span>
                    <span v-else> removed</span> from the calculated total.
                </p>

                <p>
                    During the period <strong>{{ options.numberOfWeekendDays }}</strong> number of weekend days would have
                    occurred. These have been <span v-if="options.includeWeekends"> included</span>
                    <span v-else> removed</span> from the calculated total.
                </p>

                <p>
                    The total difference between the two dates is:
                    <br><br>
                </p>

                <div class="flex-grid p-animated-calc--full-width flex-grid--row p-period-between">
                    <div v-if="options.show.years && !options.show.months && !options.show.weeks && !options.show.days">
                        <span class="p-period-between__period-result"><span class="u-bold">{{ diffYears }}</span></span>
                    </div>

                    <div v-else>
                        <span class="p-period-between__period-result"><span class="u-bold">{{ diffBetween }}</span></span>
                    </div>
                </div>
            </div>
        </div>
    </mini-calculator-page>
</template>

<script type="text/babel">
    import {mapGetters, mapActions} from 'vuex';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import DateInput from 'components/form/control/DateInput.vue';
    import MiniCalcIcons from 'components/Icons.vue'
    import Intro from 'pages/mini-calculators/calculators/CalculatorComponents/Intro';
    import MiniCalculatorPage from 'pages/mini-calculators/components/MiniCalculatorPage.vue';;
    import CalculatorKeyBindings from 'pages/mini-calculators/calculators/mixins/MiniCalculatorBindings';
    import MiniCalculatorHeader from 'pages/mini-calculators/components/MiniCalculatorHeader';
    import Moment from 'moment';

    export default {
        name: 'period-between-two-dates',
        created() {
            this.reset();
        },
        data: function() {
            return {
                showResults: false
            }
        },
        computed: {
            ...mapGetters('mini/period', ['getCalculator']),
            firstDate: {
                get() {
                    return this.getCalculator.firstDate;
                },
                set(value) {
                    this.setCalculatorValue({key: 'firstDate', value});
                }
            },
            secondDate: {
                get() {
                    return this.getCalculator.secondDate;
                },
                set(value) {
                    this.setCalculatorValue({key: 'secondDate', value});
                }
            },
            displayFormat: {
                get() {
                    return this.getCalculator.displayFormat;
                },
                set(value) {

                    this.setCalculatorValue({key: 'displayFormat', value});
                }
            },
            diffBetween: {
                get() {
                    return this.getCalculator.diffBetween;
                }
            },
            diffYears: {
                get() {
                    return this.getCalculator.diffYears;
                }
            },
            options: {
                get() {
                    return this.getCalculator.options;
                }
            },
        },
        mixins: [
            FormValidator,
            CalculatorKeyBindings
        ],
        components: {
            Control,
            ControlError,
            DateInput,
            MiniCalcIcons,
            MiniCalculatorPage,
            MiniCalculatorHeader,
            Intro
        },
        methods: {
            ...mapActions('mini/period', ['setCalculatorValue', 'calculateDifference', 'setOptions', 'reset']),
            isValid() {
                return (
                    this.firstDate !== null && Moment(this.firstDate, 'DD/MM/YYYY').isValid()
                    && this.secondDate !== null && Moment(this.secondDate, 'DD/MM/YYYY').isValid()
                    && Moment(this.firstDate, 'DD/MM/YYYY').isBefore(Moment(this.secondDate, 'DD/MM/YYYY'))
                    && !this.optionsEmpty()
                );
            },
            secondDateValidation() {
                if (this.secondDate === null || this.firstDate === null) {
                    return true;
                }
                return (
                    Moment(this.secondDate, 'DD/MM/YYYY').isValid()
                    && Moment(this.firstDate, 'DD/MM/YYYY').isValid()
                    && Moment(this.secondDate, 'DD/MM/YYYY').isBefore(Moment(this.firstDate, 'DD/MM/YYYY'))
                );
            },
            calculate() {
                if (!this.isValid()) {
                    return false;
                }

                this.hidePrompt();
                this.showResults = true;

                this.calculateDifference({
                    firstDate: this.firstDate,
                    secondDate: this.secondDate,
                    options: this.options
                });
            },
            updateOptions() {
                this.setOptions(this.options);
                this.calculate();

                if (!this.isValid()) {
                    return;
                }

                this.hidePrompt();
                this.showResults = true;
            },
            optionsEmpty() {
                return Object.keys(this.options.show).every(option => !this.options.show[option]);
            },
        }
    }
</script>



// WEBPACK FOOTER //
// periodBetweenTwoDates.vue?3c532572